import type { KeyBindings } from '../config/input.config';
import { HOLD_RELEASE, type InputAction } from './InputAction';
import type { ActionSink, InputSource } from './InputSource';

export class KeyboardInputSource implements InputSource {
  private readonly codeToAction = new Map<string, InputAction>();
  /** Currently held keys of hold actions (e.g. S and ArrowDown both slide). */
  private readonly held = new Map<InputAction, Set<string>>();
  private sink: ActionSink | null = null;

  constructor(
    bindings: KeyBindings,
    private readonly target: Window = window,
  ) {
    for (const [action, codes] of Object.entries(bindings) as [InputAction, readonly string[]][]) {
      for (const code of codes) this.codeToAction.set(code, action);
    }
  }

  attach(sink: ActionSink): void {
    this.sink = sink;
    this.target.addEventListener('keydown', this.onKeyDown);
    this.target.addEventListener('keyup', this.onKeyUp);
    this.target.addEventListener('blur', this.onBlur);
  }

  detach(): void {
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
    this.held.clear();
    this.sink = null;
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const action = this.codeToAction.get(event.code);
    if (!action) return;
    // Bound keys must not scroll the page or trigger browser shortcuts.
    event.preventDefault();
    // Holding a key must not auto-repeat lane changes/jumps.
    if (event.repeat) return;
    if (HOLD_RELEASE[action]) {
      const keys = this.held.get(action) ?? new Set<string>();
      this.held.set(action, keys);
      const alreadyHeld = keys.size > 0;
      keys.add(event.code);
      if (alreadyHeld) return; // second key of the same hold action
    }
    this.sink?.(action);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const action = this.codeToAction.get(event.code);
    const release = action ? HOLD_RELEASE[action] : undefined;
    if (!action || !release) return;
    const keys = this.held.get(action);
    if (keys?.delete(event.code) && keys.size === 0) this.sink?.(release);
  };

  /** Window lost focus: keyup events will not arrive – release everything held. */
  private readonly onBlur = (): void => {
    for (const [action, keys] of this.held) {
      const release = HOLD_RELEASE[action];
      if (keys.size > 0 && release) this.sink?.(release);
      keys.clear();
    }
  };
}
